/**
 * Project Management Service v2.0
 * Handles project data enrichment: health scores, metrics, tags, pinning, sorting,
 * activity feed, attached agents, environment switching, and bulk operations.
 */

export type ProjectEnvironment = "development" | "preview" | "staging" | "production";
export type ProjectPipelineStatus = "draft" | "building" | "deploying" | "live" | "failed";
export type SortOption = "recent" | "name" | "deployments" | "traffic" | "health" | "lastDeployed";
export type ViewMode = "grid" | "list" | "kanban";

export interface ProjectTag {
  id: string;
  label: string;
  color: string;
}

export interface ProjectMetrics {
  lastDeployedAt: string | null;
  totalDeployments: number;
  activeUsers: number;
  apiRequests: number;
  errorRate: number;
  avgLatencyMs: number;
  uptime: number;
}

export interface ProjectHealth {
  score: number;
  deploymentSuccess: number;
  errorRate: number;
  latency: number;
  uptime: number;
}

export interface ActivityEvent {
  id: string;
  message: string;
  timestamp: string;
  type: "deploy" | "optimize" | "agent" | "config" | "alert";
}

export interface AttachedAgent {
  id: string;
  name: string;
  icon: string;
}

export interface ManagedProject {
  id: number | string;
  name: string;
  description: string;
  type: string;
  status: string;
  lastModified: string;
  environment: ProjectEnvironment;
  pipelineStatus: ProjectPipelineStatus;
  health: ProjectHealth;
  metrics: ProjectMetrics;
  tags: ProjectTag[];
  pinned: boolean;
  mobileReady?: boolean;
  activity: ActivityEvent[];
  attachedAgents: AttachedAgent[];
  collaborators: string[];
}

// Predefined tag palette
export const availableTags: ProjectTag[] = [
  { id: "production", label: "Production", color: "hsl(var(--nuvra-success))" },
  { id: "internal", label: "Internal", color: "hsl(var(--nuvra-accent))" },
  { id: "client", label: "Client", color: "hsl(var(--primary))" },
  { id: "experimental", label: "Experimental", color: "hsl(var(--nuvra-warning))" },
  { id: "archived", label: "Archived", color: "hsl(var(--muted-foreground))" },
];

const ACTIVITY_TEMPLATES: { message: string; type: ActivityEvent["type"] }[] = [
  { message: "Deployed to production", type: "deploy" },
  { message: "Auto-Optimize applied", type: "optimize" },
  { message: "Agent installed: Anti-Cheat", type: "agent" },
  { message: "Config updated", type: "config" },
  { message: "Error rate spike detected", type: "alert" },
  { message: "Deployed to staging", type: "deploy" },
  { message: "Readiness scan passed (96/100)", type: "optimize" },
  { message: "Agent installed: Cost Predictor", type: "agent" },
];

const AGENT_POOL: AttachedAgent[] = [
  { id: "agent-auto-optimize", name: "Auto-Optimize", icon: "Zap" },
  { id: "agent-anti-cheat", name: "Anti-Cheat", icon: "Shield" },
  { id: "agent-cost-predictor", name: "Cost Predictor", icon: "DollarSign" },
  { id: "agent-a11y-checker", name: "A11y Auditor", icon: "Eye" },
  { id: "agent-ci-cd-auto", name: "CI/CD Auto", icon: "GitBranch" },
];

const TIMES = ["2m ago", "15m ago", "1h ago", "3h ago", "6h ago", "1d ago"];
const AVATARS = ["A", "B", "C", "D"];

function generateActivity(raw: any): ActivityEvent[] {
  return Array.isArray(raw?.activity) ? raw.activity : [];
}

function generateAgents(raw: any): AttachedAgent[] {
  return Array.isArray(raw?.attachedAgents) ? raw.attachedAgents : [];
}

function generateCollaborators(raw: any): string[] {
  return Array.isArray(raw?.collaborators) ? raw.collaborators : [];
}

function generateHealth(raw: any): ProjectHealth {
  return raw?.health || { score: 0, deploymentSuccess: 0, errorRate: 0, latency: 0, uptime: 0 };
}

function generateMetrics(raw: any): ProjectMetrics {
  return raw?.metrics || {
    lastDeployedAt: null,
    totalDeployments: 0,
    activeUsers: 0,
    apiRequests: 0,
    errorRate: 0,
    avgLatencyMs: 0,
    uptime: 0,
  };
}

function inferEnvironment(status: string): ProjectEnvironment {
  switch (status) {
    case "Active": case "Deployed": return "production";
    case "Building": return "staging";
    default: return "development";
  }
}

function inferPipelineStatus(status: string): ProjectPipelineStatus {
  switch (status) {
    case "Active": return "live";
    case "Deployed": return "live";
    case "Building": return "building";
    default: return "draft";
  }
}

/** Enrich raw project data with management fields */
export function enrichProject(raw: any): ManagedProject {
  const status = raw.status || "Draft";
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description || "",
    type: raw.type || "Web App",
    status,
    lastModified: raw.lastModified || "just now",
    environment: inferEnvironment(status),
    pipelineStatus: inferPipelineStatus(status),
    health: generateHealth(raw),
    metrics: generateMetrics(raw),
    tags: [],
    pinned: false,
    mobileReady: raw.mobileReady ?? false,
    activity: generateActivity(raw),
    attachedAgents: generateAgents(raw),
    collaborators: generateCollaborators(raw),
  };
}

/** Sort projects */
export function sortProjects(projects: ManagedProject[], by: SortOption): ManagedProject[] {
  const pinned = projects.filter((p) => p.pinned);
  const unpinned = projects.filter((p) => !p.pinned);
  const sortFn = (a: ManagedProject, b: ManagedProject) => {
    switch (by) {
      case "name": return a.name.localeCompare(b.name);
      case "deployments": return b.metrics.totalDeployments - a.metrics.totalDeployments;
      case "traffic": return b.metrics.apiRequests - a.metrics.apiRequests;
      case "health": return b.health.score - a.health.score;
      case "lastDeployed": return (b.metrics.lastDeployedAt || "z").localeCompare(a.metrics.lastDeployedAt || "z");
      default: return 0;
    }
  };
  return [...pinned.sort(sortFn), ...unpinned.sort(sortFn)];
}

// Persisted pinned/tags per project
const PINS_KEY = "nuvra-pinned-projects";
const TAGS_KEY = "nuvra-project-tags";

export function getPinnedIds(): Set<string | number> {
  try { return new Set(JSON.parse(localStorage.getItem(PINS_KEY) || "[]")); } catch { return new Set(); }
}
export function togglePin(id: string | number): void {
  const pins = getPinnedIds();
  if (pins.has(id)) pins.delete(id); else pins.add(id);
  localStorage.setItem(PINS_KEY, JSON.stringify([...pins]));
}

export function getProjectTags(id: string | number): string[] {
  try { const all = JSON.parse(localStorage.getItem(TAGS_KEY) || "{}"); return all[String(id)] || []; } catch { return []; }
}
export function setProjectTags(id: string | number, tagIds: string[]): void {
  try { const all = JSON.parse(localStorage.getItem(TAGS_KEY) || "{}"); all[String(id)] = tagIds; localStorage.setItem(TAGS_KEY, JSON.stringify(all)); } catch { /* error intentionally ignored */ }
}

/** Health score color */
export function healthColor(score: number): string {
  if (score >= 80) return "text-nuvra-success";
  if (score >= 50) return "text-nuvra-warning";
  if (score > 0) return "text-destructive";
  return "text-muted-foreground";
}

/** Pipeline status config */
export const pipelineStatusConfig: Record<ProjectPipelineStatus, { label: string; className: string; pulse?: boolean }> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground border-border" },
  building: { label: "Building", className: "bg-nuvra-warning/20 text-nuvra-warning border-nuvra-warning/30", pulse: true },
  deploying: { label: "Deploying", className: "bg-nuvra-accent/20 text-[hsl(var(--nuvra-accent))] border-[hsl(var(--nuvra-accent))]/30", pulse: true },
  live: { label: "Live", className: "bg-nuvra-success/20 text-nuvra-success border-nuvra-success/30" },
  failed: { label: "Failed", className: "bg-destructive/20 text-destructive border-destructive/30" },
};

export const environmentConfig: Record<ProjectEnvironment, { label: string; className: string }> = {
  development: { label: "Dev", className: "bg-muted text-muted-foreground" },
  preview: { label: "Preview", className: "bg-nuvra-accent/15 text-[hsl(var(--nuvra-accent))]" },
  staging: { label: "Staging", className: "bg-nuvra-warning/15 text-nuvra-warning" },
  production: { label: "Prod", className: "bg-nuvra-success/15 text-nuvra-success" },
};

export const ENVIRONMENTS: ProjectEnvironment[] = ["development", "preview", "staging", "production"];
